import React from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import useStoreToast, { ToastType, ToastAction } from "store/useStoreToast";
import { useSounds } from "context/SoundContext";
import "./index.scss";

const DISPLAY_DURATION = 2000;

type ToastItemProps = {
    id: number;
    message: React.ReactNode;
    type: ToastType;
    action?: ToastAction;
    secondaryAction?: ToastAction;
    persistent?: boolean;
    duration?: number;
};

const iconByType: Record<ToastType, React.ReactNode> = {
    success: (
        <svg width="20" height="20" viewBox="0 0 512 512" fill="#22c55e">
            <path d="M256,45.333 C139.179,45.333 45.333,139.179 45.333,256 C45.333,372.82 139.179,466.667 256,466.667 C372.82,466.667 466.667,372.82 466.667,256 C466.667,139.179 372.82,45.333 256,45.333 Z M336.336,179.781 L366.502,209.947 L234.667,342.336 L155.583,263.252 L185.75,233.086 L234.667,281.002 L336.336,179.781 Z" />
        </svg>
    ),
    error: (
        <svg width="20" height="20" viewBox="0 0 512 512" fill="#ef4444">
            <path d="M256,45.333 C373.803,45.333 469.333,140.864 469.333,258.667 C469.333,376.469 373.803,472 256,472 C138.197,472 42.667,376.469 42.667,258.667 C42.667,140.864 138.197,45.333 256,45.333 Z M304.917,176.917 L256,225.835 L207.083,176.917 L176.917,207.083 L225.835,256 L176.917,304.917 L207.083,335.083 L256,286.165 L304.917,335.083 L335.083,304.917 L286.165,256 L335.083,207.083 L304.917,176.917 Z" />
        </svg>
    ),
    info: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="#d1d5db">
            <path fillRule="evenodd" clipRule="evenodd" d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM12 17.75C12.4142 17.75 12.75 17.4142 12.75 17V11C12.75 10.5858 12.4142 10.25 12 10.25C11.5858 10.25 11.25 10.5858 11.25 11V17C11.25 17.4142 11.5858 17.75 12 17.75ZM12 7C12.5523 7 13 7.44772 13 8C13 8.55228 12.5523 9 12 9C11.4477 9 11 8.55228 11 8C11 7.44772 11.4477 7 12 7Z" />
        </svg>
    ),
};

const ToastItem = ({ id, message, type, action, secondaryAction, persistent, duration }: ToastItemProps) => {
    const remove = useStoreToast((state) => state.remove);
    const { play } = useSounds();
    const hasActions = !!action || !!secondaryAction;
    const hasTwoActions = !!action && !!secondaryAction;

    React.useEffect(() => {
        play("notification");

        if(persistent) {
            return;
        }

        const effective = duration ?? (hasActions ? DISPLAY_DURATION * 2 : DISPLAY_DURATION);
        const timer = setTimeout(() => remove(id), effective);
        return () => clearTimeout(timer);
    }, [id, remove, hasActions, persistent, duration]);

    return (
        <motion.div
            className={`toast-item toast-${type} ${hasActions ? "toast-with-action" : ""} ${persistent ? "" : "cursor-pointer"}`}
            onClick={persistent ? undefined : () => remove(id)}
            layout
            initial={{ opacity: 0, scale: 0.5, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{
                opacity: 0,
                scale: 0,
                transition: {
                    duration: 0.35,
                    ease: [0.36, 0, 0.66, -0.56],
                },
            }}
            transition={{
                type: "spring",
                stiffness: 400,
                damping: 20,
                mass: 0.8,
            }}
        >
            <div className={`toast-content ${hasActions ? "toast-content-padded" : ""}`}>
                <span className="toast-icon">{iconByType[type]}</span>
                <span className="toast-message">{message}</span>
            </div>
            {hasActions && (
                <div className={`toast-actions ${hasTwoActions ? "toast-actions-split" : ""}`}>
                    {secondaryAction && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                secondaryAction.onClick();
                                remove(id);
                            }}
                            className="toast-action toast-action-secondary"
                        >
                            {secondaryAction.label}
                        </button>
                    )}
                    {action && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                action.onClick();
                                remove(id);
                            }}
                            className="toast-action"
                        >
                            {action.label}
                        </button>
                    )}
                </div>
            )}
        </motion.div>
    );
};

const Toast = () => {
    const toasts = useStoreToast((state) => state.toasts);

    return ReactDOM.createPortal(
        <div className="toast-container">
            <AnimatePresence mode="popLayout">
                {toasts.map((toast) => (
                    <ToastItem key={toast.id} {...toast} />
                ))}
            </AnimatePresence>
        </div>,
        document.body
    );
};

export default Toast;
